import { useQuery } from '@tanstack/react-query';
import { http } from '@/api/http';
import { selectData, type SuccessResponseDTO } from '@/api/envelope';

export type InfrastructureHealth = {
  mediaManager?: {
    ready?: boolean;
    databaseConnected?: boolean | null;
    rabbitmqConnected?: boolean | null;
  };
  gpu?: {
    configured?: boolean;
    ready?: boolean;
    /** ready | offline | not_configured | unknown */
    status?: string;
    /** heuristic | shadow | real */
    deepMode?: string | null;
    /** K8s namespace hosting the GPU (e.g. "dev"); DEV+STG may share DEV GPU. */
    namespace?: string | null;
    /** Ready GPU Deployment replicas (0 when scaled to zero). */
    replicas?: number | null;
    /** Desired / max GPU Deployment replicas when known. */
    desiredReplicas?: number | null;
    /** Karpenter/EKS GPU node instance capacity (when probed). */
    capacity?: {
      readyNodes?: number | null;
      desiredNodes?: number | null;
      allocatableGpus?: number | null;
      nodePoolLimitGpus?: number | null;
      instanceTypes?: string[] | null;
      capacityTypes?: string[] | null;
      zones?: string[] | null;
      nodePool?: string | null;
      /** ready | scaled_to_zero | provisioning | unknown */
      status?: string | null;
    } | null;
  };
  coop?: {
    enabled?: boolean;
    configured?: boolean;
    ready?: boolean;
    /** ready | unreachable | disabled | not_configured | unknown */
    status?: string;
  };
  /** HOLD | LIVE_ENFORCE | OBSERVE | OFF */
  publicationMode?: string | null;
  tipmiCoopEnabled?: boolean | null;
  /** gpu | cloud | gpu_with_cloud_fallback (TIPMI_MODERATION_ENGINE) */
  moderationEngine?: string | null;
  /** @deprecated Prefer moderationEngine. Legacy bedrock|dual|gpu. */
  taxonomySource?: string | null;
  /** Always false — TipMi never auto-hides from imageHealthScore */
  imageHealthAutoHide?: boolean | null;
  outbox?: {
    pending?: number;
    dead?: number;
    /** ok | backlog | dead | unknown */
    status?: string;
  };
  crashLoops?: {
    total?: number;
    workloads?: Array<{
      id?: string | null;
      namespace?: string | null;
      deployment?: string | null;
      crashLooping?: number;
      probeOk?: boolean | null;
      pods?: Array<{
        name?: string | null;
        reason?: string | null;
        restarts?: number | null;
      }>;
    }>;
  };
  mediaManagerReachable?: boolean;
  tipmiApiReachable?: boolean;
  error?: string | null;
};

export type OutboxBacklogItem = {
  id?: number | null;
  caseId?: string | null;
  postId?: number | null;
  eventType?: string | null;
  /** PENDING | RETRY | DEAD */
  status?: string | null;
  attemptCount?: number | null;
  nextAttemptAt?: string | null;
  lastError?: string | null;
  lastStage?: string | null;
  createdAt?: string | null;
  modifiedAt?: string | null;
};

export type OutboxBacklog = {
  pending?: number;
  dead?: number;
  items?: OutboxBacklogItem[];
};

export const infrastructureHealthQueryKey = ['content', 'infrastructure-health'] as const;
export const outboxBacklogQueryKey = ['content', 'outbox-backlog'] as const;

export function useInfrastructureHealth() {
  return useQuery({
    queryKey: infrastructureHealthQueryKey,
    queryFn: async () => {
      const res = await http.get<SuccessResponseDTO<InfrastructureHealth>>(
        '/admin/api/post-manager/infrastructure-health',
      );
      return selectData<InfrastructureHealth>(res.data);
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
    retry: 1,
  });
}

export function useOutboxBacklog(enabled: boolean) {
  return useQuery({
    queryKey: outboxBacklogQueryKey,
    queryFn: async () => {
      const res = await http.get<SuccessResponseDTO<OutboxBacklog>>(
        '/admin/api/post-manager/outbox',
        { params: { limit: 50 } },
      );
      return selectData<OutboxBacklog>(res.data);
    },
    enabled,
    staleTime: 5_000,
    refetchInterval: enabled ? 15_000 : false,
    retry: 1,
  });
}
